import { getLocale } from '../foundation';
import { LocalDataKeys } from '@libs/types';
import { useCallback } from 'react';
import { useLocalData } from './useLocalData';

/**
 * Lightweight, file-based localization for the player.
 *
 * The translations dictionary is bundled straight into the JS build: a package owns its
 * `translations.json` (e.g. `packages/consumer/translations.json`), `import`s it and seeds
 * this module once via {@link setTranslations} at startup. It is therefore available
 * synchronously on the very first render — nothing is fetched over the network and nothing
 * is read from a database, so there is no async gap, no flash of fallback text and no
 * offline-caching machinery to maintain.
 *
 * If the dictionary was never seeded (or a key is missing), `t()` falls back to English,
 * then the first available language, then the provided fallback, then the key itself.
 *
 * The JSON is key-first so per-key fallback is trivial:
 * ```json
 * { "button.pay": { "en": "Pay", "ru": "Оплатить" } }
 * ```
 */

/** `lang -> text` for a single key. */
type TTranslationEntry = Record<string, string>;
/** `key -> { lang -> text }`. */
export type TTranslations = Record<string, TTranslationEntry>;

/** Language returned when the current one has no value for a key. */
const DEFAULT_LANG = 'en';

/** Stable empty dictionary returned before seeding, so hook consumers get a constant reference. */
const EMPTY_TRANSLATIONS: TTranslations = {};

/** Populated once via {@link setTranslations}; read synchronously by {@link getTranslationsSnapshot}. */
let memoryCache: TTranslations | null = null;

/** Synchronously returns the seeded translations, or `null` if none were provided yet. */
export const getTranslationsSnapshot = (): TTranslations | null => memoryCache;

const isTranslations = (value: unknown): value is TTranslations => typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Seeds the bundled translations dictionary. Call once at app startup with the statically
 * imported `translations.json`, before anything renders, e.g.:
 *
 * ```ts
 * import translations from '../translations.json';
 * setTranslations(translations);
 * ```
 *
 * Ignores anything that is not a plain object so a malformed import can never break the UI —
 * `t()` simply falls back to the key/fallback until valid translations are supplied.
 */
export const setTranslations = (dict: unknown): void => {
    if (isTranslations(dict)) {
        memoryCache = dict;
    }
};

// --- Resolution -------------------------------------------------------------------------

/**
 * Normalizes a locale (`en-US`, `RU`) to a short lower-case language tag (`en`, `ru`).
 * Non-string input (e.g. a stray boolean/number persisted under the `lang` key) falls back
 * to {@link DEFAULT_LANG} rather than throwing — a bad locale value must never crash render.
 */
export const normalizeLang = (locale: string | null | undefined): string =>
    typeof locale === 'string' && locale ? locale.split('-')[0].toLowerCase() : DEFAULT_LANG;

/**
 * Resolves a key against a translations dictionary, falling back in order:
 * current language → English → first available language → `fallback` → the key itself.
 */
export const resolveTranslation = (dict: TTranslations, key: string, lang: string, fallback?: string): string => {
    const entry = dict[key];
    if (entry) {
        if (entry[lang]) {
            return entry[lang];
        }
        if (entry[DEFAULT_LANG]) {
            return entry[DEFAULT_LANG];
        }
        const first = Object.values(entry).find(Boolean);
        if (first) {
            return first;
        }
    }
    return fallback ?? key;
};

type TUseTranslation = {
    /** Translates `key`, using `fallback` (then the key) when no value is found. */
    t: (key: string, fallback?: string) => string;
    /** The resolved, normalized current language (e.g. `en`). */
    lang: string;
};

/**
 * React hook returning a `t(key)` translator bound to the current language.
 *
 * The current language comes from the `lang` local-data key (reactively, so switching
 * language re-renders consumers) and falls back to {@link getLocale}. The dictionary is
 * read synchronously from the bundled snapshot ({@link getTranslationsSnapshot}), so `t()`
 * is correct on the first render as long as {@link setTranslations} ran at startup.
 */
export const useTranslation = (): TUseTranslation => {
    const storedLang = useLocalData<string>(LocalDataKeys.lang);
    const lang = normalizeLang(storedLang ?? getLocale());

    // The dictionary is seeded once, synchronously, before render (see setTranslations), so a
    // direct read of the snapshot is enough — no effect / async re-render is needed. The two
    // possible values (the seeded cache or EMPTY_TRANSLATIONS) are both stable references.
    const dict = getTranslationsSnapshot() ?? EMPTY_TRANSLATIONS;

    const t = useCallback((key: string, fallback?: string) => resolveTranslation(dict, key, lang, fallback), [dict, lang]);

    return { t, lang };
};

/**
 * Test-only: clears the seeded dictionary. Not part of the public runtime API.
 */
export const __resetTranslationsForTests = (): void => {
    memoryCache = null;
};
